import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

export interface ScrollSelectOption {
	value: any;
	label: string;
	/** Optional heading this option sits under, mirroring <optgroup>. */
	group?: string;
}

/**
 * Drop-in replacement for a long native <select>.
 *
 * A native select's popup is drawn by the operating system, so its height cannot
 * be capped from CSS - a 64-entry year list covers the whole window on macOS.
 * This renders its own menu instead, capped at ten rows with scrolling, and
 * flips above the field when there is no room below.
 */
@Component({
	selector: 'scroll-select',
	templateUrl: './scroll-select.component.html',
	styleUrls: ['./scroll-select.component.scss']
})
export class ScrollSelectComponent {
	@Input() options: ScrollSelectOption[] = [];
	@Input() value: any = null;
	@Input() placeholder: string = 'Select';
	@Input() disabled: boolean = false;
	@Input() ariaLabel: string = '';
	/** Draws the field in its error state, replacing a native select's .error class. */
	@Input() invalid: boolean = false;
	@Output() valueChange = new EventEmitter<any>();

	public open: boolean = false;
	public dropUp: boolean = false;

	constructor(private hostRef: ElementRef) { }

	get selectedLabel(): string {
		const match = this.options.find(o => `${o.value}` === `${this.value}`);
		return match ? match.label : this.placeholder;
	}

	get hasSelection(): boolean {
		return this.options.some(o => `${o.value}` === `${this.value}`);
	}

	toggle() {
		if (this.disabled) return;
		this.open = !this.open;
		if (this.open) {
			this.decideDirection();
			// bring the selected row into view without animating the whole list
			setTimeout(() => {
				const menu = this.hostRef.nativeElement.querySelector('.ss-menu');
				const active = menu?.querySelector('.ss-option.selected');
				if (menu && active) {
					menu.scrollTop = active.offsetTop - menu.clientHeight / 2 + active.clientHeight / 2;
				}
			}, 0);
		}
	}

	select(option: ScrollSelectOption) {
		this.value = option.value;
		this.valueChange.emit(option.value);
		this.open = false;
	}

	isSelected(option: ScrollSelectOption): boolean {
		return `${option.value}` === `${this.value}`;
	}

	/** A heading is drawn only on the first option of each group, so grouped and
	 *  ungrouped lists share one @for. */
	showGroupHeader(option: ScrollSelectOption, index: number): boolean {
		if (!option.group) return false;
		return index === 0 || this.options[index - 1]?.group !== option.group;
	}

	/** Ten rows is the cap, so the menu is a known height before it renders. */
	private decideDirection() {
		const trigger = this.hostRef.nativeElement.querySelector('.ss-trigger');
		if (!trigger) return;
		const rect = trigger.getBoundingClientRect();
		const menuHeight = 10 * 34 + 8;
		const spaceBelow = window.innerHeight - rect.bottom;
		this.dropUp = spaceBelow < menuHeight && rect.top > spaceBelow;
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		if (!this.open) return;
		if (!this.hostRef.nativeElement.contains(event.target)) {
			this.open = false;
		}
	}

	@HostListener('keydown.escape')
	onEscape() {
		this.open = false;
	}
}
